// src/components/dashboard/cases/PatientRecord.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaRegFileAlt, FaLink, FaSort } from "react-icons/fa";
import Header from "../../header/header.js";
import Navvbar from "../../header/Navvbar.js";
import axios from "axios";

const PatientRecord = () => {
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [relatedCases, setRelatedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString("en-US");
  };

  // Fetch accounts (for account click navigation)
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get(
          "https://hms.backend.hiragroupcloud.com/api/account/account"
        );
        const accountsData = response.data.data;
        const formattedAccounts = Array.isArray(accountsData)
          ? accountsData.map((account) => ({
              _id: account._id,
              name: account.account_name,
              accountId: account.accountId,
              account_type: account.account_type,
              city: account.city,
              zip: account.zip,
              address: account.address,
              telephone: account.telephone,
              address2: account.address2,
              email: account.email,
              country: account.country,
              website: account.website,
              state: account.state,
              time_zone: account.time_zone,
              status: account.status,
              ddxEnable: account.ddxEnable,
              createdAt: account.createdAt,
              updatedAt: account.updatedAt,
            }))
          : [];
        setAccounts(formattedAccounts);
      } catch (error) {
        console.error("Error fetching accounts:", error);
        setAccounts([]);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch related cases (original + remake)
  useEffect(() => {
    const fetchRelatedCases = async () => {
      setLoading(true);
      try {
        const caseNumber = sessionStorage.getItem("currentCaseNumber");

        if (!caseNumber) {
          throw new Error("Case number not found in session");
        }

        const res = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseNumber}/related-remakes`
        );

        if (res.data?.success) {
          setRelatedCases(res.data.data || []);
          setPatientInfo(res.data.patientInfo || null);
        } else {
          throw new Error(res.data?.message || "Failed to load cases");
        }
      } catch (err) {
        console.error("Error fetching related cases:", err);
        setError(
          err.response?.data?.message || err.message || "Failed to load cases"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedCases();
  }, []);

  const handleAccountClick = () => {
    if (!patientInfo?.accountId) return;

    const account = accounts.find(
      (acc) =>
        acc.accountId === patientInfo.accountId ||
        acc.account_name?.toLowerCase() === patientInfo.accountName?.toLowerCase()
    );

    if (account) {
      navigate(`/tabsWorking/${account.accountId}`, {
        state: { accountData: account },
      });
    } else {
      navigate(`/tabsWorking/account`, {
        state: { accountName: patientInfo.accountName },
      });
    }
  };

  const linkStyle = (id) => ({
    color: hoveredLink === id ? "#002a66" : "#004aad",
    textDecoration: hoveredLink === id ? "underline" : "none",
    transition: "color 0.2s ease, text-decoration 0.2s ease",
    cursor: "pointer",
  });

  const rowStyle = (id) => ({
    borderTop: "1px solid #dee2e6",
    backgroundColor: hoveredRow === id ? "#f1f3f5" : "transparent",
    transition: "background-color 0.2s ease",
  });

  if (loading) {
    return (
      <div className="container-fluid" style={{ padding: "1.5rem" }}>
        <Header />
        <Navvbar />
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading patient record...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid" style={{ padding: "1.5rem" }}>
        <Header />
        <Navvbar />
        <div
          className="alert alert-danger"
          style={{ margin: "1.5rem auto", maxWidth: "95%" }}
        >
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div
      className="container-fluid"
      style={{
        padding: "1.5rem",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Header + Navbar */}
      <div style={{ marginBottom: "1.5rem" }}>
        <Header />
        <Navvbar />
      </div>

      {/* ==== Patient Record Section ==== */}
      <div
        style={{
          width: "95%",
          margin: "0 auto",
          backgroundColor: "#ffffff",
          paddingBottom: "0.5rem",
        }}
      >
        {/* Title */}
        <h2
          style={{
            fontSize: "1.75rem",
            fontWeight: "700",
            color: "#1a1a1a",
            margin: "0 0 12px 0",
            padding: "0 0 10px 0",
            borderBottom: "3px solid #004aad",
          }}
        >
          Patient Record
        </h2>

        {/* Patient Info Box — grey background */}
        {patientInfo && (
          <div
            style={{
              backgroundColor: "#f1f3f5",
              padding: "20px 25px",
              borderRadius: "4px",
              marginTop: "15px",
            }}
          >
            <div className="row">
              {/* Left Column */}
              <div className="col-md-6" style={{ marginBottom: "0.5rem" }}>
                <p
                  style={{
                    marginBottom: "0.75rem",
                    fontSize: "0.95rem",
                    color: "#1a1a1a",
                  }}
                >
                  <strong style={{ fontWeight: "600" }}>First Name:</strong>
                  <br />
                  <span style={{ marginLeft: "15px" }}>
                    {patientInfo.firstName || "Not Available"}
                  </span>
                </p>
                <p
                  style={{
                    marginBottom: "0.75rem",
                    fontSize: "0.95rem",
                    color: "#1a1a1a",
                  }}
                >
                  <strong style={{ fontWeight: "600" }}>Last Name:</strong>
                  <br />
                  <span style={{ marginLeft: "15px" }}>
                    {patientInfo.lastName || "Not Available"}
                  </span>
                </p>
                <p
                  style={{
                    marginBottom: "0.75rem",
                    fontSize: "0.95rem",
                    color: "#1a1a1a",
                  }}
                >
                  <strong style={{ fontWeight: "600" }}>Date of Birth:</strong>
                  <br />
                  <span style={{ marginLeft: "15px" }}>
                    {patientInfo.birthDate
                      ? new Date(patientInfo.birthDate).toLocaleDateString()
                      : "Not Entered"}
                  </span>
                </p>
              </div>

              {/* Right Column */}
              <div className="col-md-6" style={{ marginBottom: "0.5rem" }}>
                <p
                  style={{
                    marginBottom: "0.75rem",
                    fontSize: "0.95rem",
                    color: "#1a1a1a",
                  }}
                >
                  <strong style={{ fontWeight: "600" }}>Sex:</strong>
                  <br />
                  <span style={{ marginLeft: "15px" }}>
                    {patientInfo.selectedGender || "Not Available"}
                  </span>
                </p>
                <p
                  style={{
                    marginBottom: "0.75rem",
                    fontSize: "0.95rem",
                    color: "#1a1a1a",
                  }}
                >
                  <strong style={{ fontWeight: "600" }}>External ID:</strong>
                  <br />
                  <span style={{ marginLeft: "15px" }}>
                    {patientInfo.externalId || "Not Entered"}
                  </span>
                </p>
                <p
                  style={{
                    marginBottom: "0.75rem",
                    fontSize: "0.95rem",
                    color: "#1a1a1a",
                  }}
                >
                  <strong style={{ fontWeight: "600" }}>Account:</strong>
                  <br />
                  <span
                    style={{
                      ...linkStyle("account"),
                      marginLeft: "15px",
                      color: "#004aad",
                    }}
                    onMouseEnter={() => setHoveredLink("account")}
                    onMouseLeave={() => setHoveredLink(null)}
                    onClick={handleAccountClick}
                  >
                    {patientInfo.accountName || "Not Available"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ==== Cases Tab ==== */}
        <div
          style={{
            display: "inline-block",
            backgroundColor: "#004aad",
            color: "#ffffff",
            fontWeight: "600",
            fontSize: "1rem",
            padding: "12px 30px",
            borderRadius: "4px 4px 0 0",
            marginTop: "25px",
            position: "relative",
            zIndex: 2,
          }}
        >
          Cases
        </div>

        {/* Blue line under tab */}
        <div
          style={{
            borderBottom: "3px solid #004aad",
            marginTop: "-3px",
            marginBottom: "0",
          }}
        />

        {/* Summary line */}
        <div
          style={{
            padding: "15px 0",
            fontSize: "0.9rem",
            fontWeight: "600",
            color: "#1a1a1a",
          }}
        >
          Showing 1 to {relatedCases.length} of {relatedCases.length} entries
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.9rem",
            }}
          >
            <thead
              style={{
                backgroundColor: "#f1f3f5",
                color: "#1a1a1a",
                fontWeight: "700",
                borderTop: "1px solid #dee2e6",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <tr>
                <th style={{ padding: "14px 12px", width: "5%", textAlign: "left" }}>
                  <FaRegFileAlt />
                </th>
                <th style={{ padding: "14px 12px", width: "5%", textAlign: "left" }}>
                  <FaLink />
                </th>
                <th style={{ padding: "14px 12px", textAlign: "left" }}>
                  Case <FaSort />
                </th>
                <th style={{ padding: "14px 12px", textAlign: "left" }}>
                  Invoice <FaSort />
                </th>
                <th style={{ padding: "14px 12px", textAlign: "left" }}>
                  Provider <FaSort />
                </th>
                <th style={{ padding: "14px 12px", textAlign: "left" }}>
                  Appt <FaSort />
                </th>
                <th style={{ padding: "14px 12px", textAlign: "left" }}>
                  Account Ship <FaSort />
                </th>
                <th style={{ padding: "14px 12px", textAlign: "left" }}>
                  Lab Ship <FaSort />
                </th>
                <th style={{ padding: "14px 12px", textAlign: "left" }}>
                  Delivery <FaSort />
                </th>
              </tr>
            </thead>
            <tbody>
              {relatedCases.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#6c757d",
                    }}
                  >
                    No cases found
                  </td>
                </tr>
              ) : (
                relatedCases.map((caseItem, index) => (
                  <tr
                    key={caseItem._id}
                    onMouseEnter={() => setHoveredRow(`case${index}`)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={rowStyle(`case${index}`)}
                  >
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      <FaRegFileAlt />
                    </td>
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      <FaLink />
                    </td>
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      <Link
                        to={`/case/${caseItem.case_id}`}
                        style={{
                          ...linkStyle(`case${index}`),
                          fontWeight: "600",
                        }}
                        onMouseEnter={() => setHoveredLink(`case${index}`)}
                        onMouseLeave={() => setHoveredLink(null)}
                      >
                        #{caseItem.caseNumber}
                      </Link>
                      <div
                        style={{
                          color: "#6c757d",
                          fontSize: "0.85rem",
                          marginTop: "2px",
                        }}
                      >
                        {caseItem.workRequested}
                      </div>
                    </td>
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      {caseItem.invoiceNumber ? (
                        <Link
                          to={`/invoices/${caseItem.invoiceNumber}`}
                          style={linkStyle(`invoice${index}`)}
                          onMouseEnter={() => setHoveredLink(`invoice${index}`)}
                          onMouseLeave={() => setHoveredLink(null)}
                        >
                          {caseItem.invoiceNumber}
                        </Link>
                      ) : (
                        "N/A"
                      )}
                      <br />
                      {caseItem.procedures && caseItem.procedures.length > 0
                        ? `£${caseItem.procedures
                            .reduce((total, proc) => {
                              const teethCount = proc.teeth
                                ? proc.teeth.split(",").filter((t) => t.trim()).length
                                : 0;
                              return total + (proc.price || 0) * teethCount;
                            }, 0)
                            .toFixed(2)}`
                        : "N/A"}
                    </td>
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      {caseItem.providerDisplay || "N/A"}
                    </td>
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      {formatDate(caseItem.ship_date)}
                    </td>
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      {formatDate(caseItem.start_date)}
                    </td>
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      {formatDate(caseItem.arrive_date)}
                    </td>
                    <td style={{ padding: "18px 12px", verticalAlign: "middle" }}>
                      {formatDate(caseItem.delivery_date)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Bottom summary */}
        <div
          style={{
            borderTop: "3px solid #004aad",
            marginTop: "20px",
            paddingTop: "12px",
            fontSize: "0.9rem",
            fontWeight: "600",
            color: "#1a1a1a",
          }}
        >
          Showing 1 to {relatedCases.length} of {relatedCases.length} entries
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: "2rem" }}>
        <footer />
      </div>
    </div>
  );
};

export default PatientRecord;